// useShuttleAreas — Wave 14dt
// ============================
//
// Hook der die ShuttleArea-Liste (PL/KA/IS/ME/BN initial) vom Backend lädt
// und cached. Wird vom MapPage (Shuttle-Mode-Rendering) und ggf. später
// vom Admin-SPA-Run-Edit-Form (Dropdown) genutzt.
//
// Cache-Strategie: einfacher Module-Level-Memo. Areas ändern sich selten
// (nur Admin-CRUD), kein TTL nötig — Page-Reload holt frische Liste.
// Bei Netz-Fehler oder leerer Antwort returnen wir [] (kein Crash, kein
// "loading"-Status länger als 1 Sekunde).

import { useEffect, useState } from 'react';
import type { ShuttleArea } from '@adtu/shared';
import { listShuttleAreas } from './api';

let cachedAreas: ShuttleArea[] | null = null;
let inFlightPromise: Promise<ShuttleArea[]> | null = null;

async function fetchAreasCached(): Promise<ShuttleArea[]> {
  if (cachedAreas) return cachedAreas;
  if (inFlightPromise) return inFlightPromise;
  inFlightPromise = listShuttleAreas().then((areas) => {
    cachedAreas = areas;
    inFlightPromise = null;
    return areas;
  });
  return inFlightPromise;
}

export function useShuttleAreas(): {
  areas: ShuttleArea[];
  loading: boolean;
} {
  const [areas, setAreas] = useState<ShuttleArea[]>(cachedAreas ?? []);
  const [loading, setLoading] = useState(cachedAreas === null);

  useEffect(() => {
    let cancelled = false;
    fetchAreasCached().then((list) => {
      if (cancelled) return;
      setAreas(list);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return { areas, loading };
}

/** Reset zum Force-Refresh (z.B. nach Admin-CRUD). Aktuell nirgendwo
 *  gecallt — wird relevant wenn der Admin-Tab kommt und Areas mutieren
 *  kann. */
export function invalidateShuttleAreasCache(): void {
  cachedAreas = null;
  inFlightPromise = null;
}
